import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Plus } from "lucide-react";
import { Reveal, Overline, EASE } from "@/lib/Reveal";
import { GALLERY_CATEGORIES, PROJECTS } from "@/lib/site";

export default function ProjectsGallery() {
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);

    const visible = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

    useEffect(() => {
        if (!active) return;
        const onKey = (e) => e.key === "Escape" && setActive(null);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active]);

    return (
        <section id="projects" data-testid="projects-section" className="py-20 lg:py-28 bg-[#FAFAFA]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                    <Reveal>
                        <Overline>Recent Projects</Overline>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-neutral-900 mt-4 leading-[1.05]">
                            Work we are proud of
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="text-base text-neutral-600 leading-relaxed max-w-md">
                            A snapshot of projects across Luton. Placeholder imagery shown — real project photos are
                            added as jobs are completed.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <div className="flex flex-wrap gap-2.5 mb-10" role="tablist" aria-label="Filter projects">
                        {GALLERY_CATEGORIES.map((c) => (
                            <button
                                key={c.id}
                                data-testid={c.testid}
                                onClick={() => setFilter(c.id)}
                                role="tab"
                                aria-selected={filter === c.id}
                                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-[background-color,color,border-color] duration-200 border ${
                                    filter === c.id
                                        ? "bg-[#141414] text-white border-[#141414]"
                                        : "bg-white text-neutral-600 border-[#E5E5E0] hover:border-neutral-900 hover:text-neutral-900"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <AnimatePresence mode="popLayout">
                        {visible.map((p) => (
                            <motion.button
                                layout
                                key={p.id}
                                data-testid="gallery-item-card"
                                initial={{ opacity: 0, scale: 0.94 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.45, ease: EASE }}
                                onClick={() => setActive(p)}
                                className="group relative rounded-2xl overflow-hidden text-left bg-[#141414] aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-[#E85A1D]"
                                aria-label={`View project: ${p.title}`}
                            >
                                <img
                                    src={p.img}
                                    alt={p.alt}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/90 via-[#141414]/20 to-transparent" />
                                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <Plus className="w-5 h-5 text-white" />
                                </div>
                                <div className="absolute bottom-0 inset-x-0 p-6">
                                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[#E85A1D] font-semibold">
                                        {GALLERY_CATEGORIES.find((c) => c.id === p.category)?.label}
                                    </p>
                                    <h3 className="font-display text-xl font-bold text-white mt-1.5 tracking-tight">{p.title}</h3>
                                    <p className="text-white/60 text-xs mt-1 flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5" />
                                        {p.location}
                                    </p>
                                </div>
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        data-testid="gallery-lightbox-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[80] bg-[#141414]/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
                        onClick={() => setActive(active ? null : null)}
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Project details: ${active.title}`}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 40, scale: 0.96 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            className="relative max-w-4xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                data-testid="gallery-lightbox-close-button"
                                onClick={() => setActive(null)}
                                className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-[#141414]/70 backdrop-blur-md flex items-center justify-center text-white hover:bg-[#E85A1D] transition-colors duration-200"
                                aria-label="Close project view"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <img src={active.img} alt={active.alt} className="w-full h-72 sm:h-96 object-cover" />
                            <div className="p-7 sm:p-9">
                                <p className="font-mono-brand text-[11px] uppercase tracking-[0.25em] text-[#E85A1D] font-semibold">
                                    {GALLERY_CATEGORIES.find((c) => c.id === active.category)?.label} · {active.location}
                                </p>
                                <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-neutral-900 mt-2">
                                    {active.title}
                                </h3>
                                <p className="mt-3 text-sm sm:text-base text-neutral-600 leading-relaxed">{active.desc}</p>
                                <p className="mt-4 text-xs text-neutral-400 italic">
                                    Placeholder image — real project photography will replace this as jobs are completed.
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
